import { useMemo, useState } from "react";
import { useSnapshot } from "valtio";
import { appStore } from "src/store/appStore";
import TouchEditor from "src/modules/engagement/components/phases/emailSms/TouchEditor";
import MessagesTab from "./MessagesTab";
import SequencesTab from "./SequencesTab";
import { useCatalog } from "./useCatalog";
import "src/modules/engagement/components/phases/emailSms/emailSms.scss";
import "./emailSmsCatalog.scss";

type Tab = "messages" | "sequences";

/*
    Internal to Motimatic: the templates and sequences every client's Email & SMS campaigns
    draw from. A client never sees this — what they approve reaches them through content
    approval instead.
*/
export default function EmailSmsCatalogPage() {
  /*
      Internal to Motimatic, and restricted to Ops and super-admin while the feature is under
      test (MPN-2197). Checked on the page rather than only in the nav, because the route is
      reachable by URL and nothing links to it yet — the server gates the same reads on the
      same feature.
  */
  const { loggedInUser } = useSnapshot(appStore);
  const canSee = loggedInUser?.permissions?.hasAccess?.("Email & SMS") ?? false;

  const [tab, setTab] = useState<Tab>("messages");
  const [sequenceId, setSequenceId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  /*
      Rows, not a fixture (MPN-2201). Edits used to live in page state and were lost on
      reload; they go to the server now, which is also what makes the catalog agree with the
      campaigns reading from it.
  */
  const { sequences, stages, preview, isLoading, error, editTouch, setPublished } = useCatalog();

  const stageOptions = useMemo(() => stages.map((stage) => stage.name), [stages]);
  const usedIn = useMemo(
    () => Object.fromEntries(sequences.map((s) => [s.id, s.usedIn])),
    [sequences],
  );

  const editingSequence = sequences.find((s) => s.touches.some((t) => t.id === editingId));
  const editingTouch = editingSequence?.touches.find((t) => t.id === editingId) ?? null;

  if (!canSee) {
    return (
      <div className="cat">
        <div className="cat__denied">
          <i className="bi bi-lock" />
          <div className="cat__denied-title">
            You don't have access to the Email &amp; SMS Catalog
          </div>
          <p className="cat__denied-body">
            It's internal to Motimatic. Email and text content your school approves shows up in
            content approval instead.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="cat">
      <div className="cat__head">
        <div className="cat__head-top">
          <span className="cat__accent" />
          <div className="cat__ident">
            <h1>Email &amp; SMS Catalog</h1>
            <div className="cat__sub">
              Templates and sequences used by Email &amp; SMS campaigns. Internal only.
            </div>
          </div>
          <div className="cat__actions">
            {tab === "messages" ? (
              <>
                <button type="button" className="btn btn-outline-primary btn-sm" disabled>
                  Import HTML
                </button>
                <button type="button" className="btn btn-primary btn-sm" disabled>
                  + New message
                </button>
              </>
            ) : (
              <span
                className="cat__new-seq"
                title="Coming later. One playbook per funnel stage for now."
              >
                <button type="button" className="btn btn-primary btn-sm" disabled>
                  + New sequence
                </button>
              </span>
            )}
          </div>
        </div>

        <div className="cat__tabs" role="tablist">
          {(["messages", "sequences"] as Tab[]).map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={tab === option}
              className={`cat__tab${tab === option ? " cat__tab--on" : ""}`}
              onClick={() => setTab(option)}
            >
              {option === "messages" ? "Messages" : "Sequences"}
            </button>
          ))}
        </div>
      </div>

      <div className="cat__body">
        {error && <div className="cat__error">{error}</div>}
        {isLoading ? (
          <div className="cat__loading">Loading the catalog…</div>
        ) : tab === "messages" ? (
          <MessagesTab
            onOpen={setEditingId}
            sequences={sequences}
            stageOptions={stageOptions}
            preview={preview}
          />
        ) : (
          <SequencesTab
            selectedId={sequenceId}
            onSelect={setSequenceId}
            onOpenTouch={setEditingId}
            sequences={sequences}
            usedIn={usedIn}
            stageOptions={stageOptions}
            isBusy={isBusy}
            onSetPublished={(id, published) => {
              setIsBusy(true);
              void setPublished(id, published).finally(() => setIsBusy(false));
            }}
          />
        )}
      </div>

      {editingTouch && editingSequence && (
        <TouchEditor
          touch={editingTouch}
          siblings={editingSequence.touches}
          keyDates={[]}
          crumb={`Email & SMS Catalog › ${editingSequence.name}`}
          scope="catalog"
          // Tokens stay unresolved: a catalog message belongs to no campaign, so there are
          // no real values, and a template is what it reads as.
          values={preview?.sample_values ?? {}}
          fromName={preview?.from_name ?? ""}
          fromAddress={preview?.from_address ?? ""}
          smsSender={preview?.sms_sender ?? ""}
          footerAddress={preview?.footer_address ?? ""}
          onSave={(next) => {
            const touchId = Number(editingTouch.id.split("-")[1]);
            setIsBusy(true);
            void editTouch(Number(editingSequence.id), touchId, {
              title: next.title,
              subject: next.subject,
              preheader: next.pre,
              html: next.body,
              text: next.text,
            } as Record<string, string>)
              .then((ok) => {
                if (ok) setEditingId(null);
              })
              .finally(() => setIsBusy(false));
          }}
          onStep={setEditingId}
          onClose={() => setEditingId(null)}
        />
      )}
    </div>
  );
}
